import {
  AI_MAX_DAILY_TOKEN_BUDGET,
  AI_PROVIDER_KINDS,
  AiProviderKind,
  OPENAI_COMPATIBLE_PRESETS,
  AiRedaction,
  AiRouteTask,
  type AiCallStatus,
  type AiProviderInput,
  type AiProviderView,
  type AiRoute,
  type AiSettingsView,
} from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import {
  createAiProvider,
  deleteAiProvider,
  getAiSettings,
  getAiUsage,
  listAiProviders,
  listAiRoutes,
  saveAiRoutes,
  saveAiSettings,
  testAiProvider,
  updateAiProvider,
} from '../api/ai.js';
import { needsReauth } from '../api/client.js';
import { useReauth } from '../auth/reauth-context.js';
import { useCurrentUser } from '../auth/session-context.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { EmptyState } from '../components/EmptyState.js';
import { PageHeader } from '../components/PageHeader.js';
import { effectiveTimezone, formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { SelectField, TextField } from '../ui/Field.js';

/**
 * AI (knowledge doc section 13; P9.2): whether it is used at all, the providers,
 * which model each task goes to, what is masked first, and what it has cost.
 *
 * Every change here asks for the re-check: section 12 rates changing AI
 * providers high, and the rest decides as much what leaves the system. Keys are
 * write-only, as the API keeps them.
 */

const errorText = (caught: unknown, fallback: string): string =>
  needsReauth(caught)
    ? 'That needed a fresh confirmation. Try again.'
    : caught instanceof Error
      ? caught.message
      : fallback;

const REDACTION_LABELS: Record<AiRedaction, string> = {
  ips: 'IP addresses',
  usernames: 'User names (not system accounts such as root)',
  internal_hostnames: "Your servers' names and their domain",
  paths: 'File and URL paths',
};

/** Each kind as the form names it: the shared catalogue's label, a local model said so. */
const kindLabel = (kind: AiProviderKind): string =>
  kind === 'ollama' ? 'Ollama (local model)' : AI_PROVIDER_KINDS[kind].label;

/** Where a provider's requests go when no address is stored. */
const addressOf = (provider: AiProviderView): string =>
  provider.baseUrl ?? AI_PROVIDER_KINDS[provider.kind].origin ?? '—';

const TASK_LABELS: Record<AiRouteTask, { label: string; hint: string }> = {
  default: { label: 'Default', hint: 'Every task without a route of its own.' },
  explain_event: { label: 'Explain an event', hint: 'The Explain button on an event.' },
  summarize_events: { label: 'Summaries', hint: 'Not used yet.' },
  incident_analysis: { label: 'Incident analysis', hint: 'Not used yet.' },
  ask: { label: 'Questions', hint: 'Not used yet.' },
};

const tokens = (n: number): string => n.toLocaleString('en');

const STATUS_BADGE: Record<AiCallStatus, string> = {
  ok: 'badge-success',
  running: 'badge-muted',
  refused: 'badge-warning',
  failed: 'badge-danger',
};

export function AiPage(): ReactNode {
  const settings = useQuery({ queryKey: ['ai-settings'], queryFn: getAiSettings });
  const providers = useQuery({ queryKey: ['ai-providers'], queryFn: listAiProviders });
  const routes = useQuery({ queryKey: ['ai-routes'], queryFn: listAiRoutes });

  return (
    <div className="page">
      <PageHeader
        title="AI"
        description="Advice from a language model, never action: a person reads it and decides. Choose the providers, which model each task uses, and what is masked before anything is sent."
      />
      {settings.data !== undefined && <SettingsCard current={settings.data} />}
      <ProvidersCard providers={providers.data ?? []} />
      {routes.data !== undefined && providers.data !== undefined && (
        <RoutingCard routes={routes.data} providers={providers.data} />
      )}
      <UsageCard />
    </div>
  );
}

function SettingsCard({ current }: { current: AiSettingsView }): ReactNode {
  const { ensureReauthenticated } = useReauth();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(current.enabled);
  const [budget, setBudget] = useState(String(current.dailyTokenBudget));
  const [redact, setRedact] = useState<AiRedaction[]>(current.redact);
  const [autoSuggest, setAutoSuggest] = useState(current.autoSuggest);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      saveAiSettings({ enabled, dailyTokenBudget: Number(budget), redact, autoSuggest }),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['ai-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['ai-status'] });
    },
    onError: (caught: unknown) => {
      setError(errorText(caught, 'Could not save the AI settings.'));
    },
  });

  const submit = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSaved(false);
    if (await ensureReauthenticated()) save.mutate();
  };

  return (
    <Card title="Settings">
      {!current.encryptionAvailable && (
        <Alert tone="warning">
          This installation has no encryption key, so no provider key can be stored. Only a local
          model without a key can be used.
        </Alert>
      )}
      <form onSubmit={(event) => void submit(event)} className="stack">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => {
              setEnabled(event.target.checked);
            }}
          />
          Use AI
        </label>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={autoSuggest}
            onChange={(event) => {
              setAutoSuggest(event.target.checked);
            }}
          />
          Suggest responses to attacks as they arrive (spends the budget without anyone asking)
        </label>
        <TextField
          label="Daily token budget"
          hint="Tokens a day (UTC) across metered providers. A local model is not counted, so it keeps working when this is spent. 0 leaves only local models."
          type="number"
          min={0}
          max={AI_MAX_DAILY_TOKEN_BUDGET}
          required
          value={budget}
          onChange={(event) => {
            setBudget(event.target.value);
          }}
        />
        <fieldset className="fieldset stack">
          <legend>Mask before sending</legend>
          <span className="field-hint">
            Masked values become placeholders such as [ip-1], the same one each time, and are put
            back in what the model answers.
          </span>
          {AiRedaction.options.map((option) => (
            <label key={option} className="checkbox">
              <input
                type="checkbox"
                checked={redact.includes(option)}
                onChange={(event) => {
                  const on = event.target.checked;
                  setRedact((list) =>
                    on ? [...list, option] : list.filter((each) => each !== option),
                  );
                }}
              />
              {REDACTION_LABELS[option]}
            </label>
          ))}
        </fieldset>
        {error !== null && <Alert tone="danger">{error}</Alert>}
        {saved && <Alert tone="success">Saved.</Alert>}
        <div className="row">
          <Button type="submit" variant="primary" busy={save.isPending} busyLabel="Saving…">
            Save AI settings
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ProvidersCard({ providers }: { providers: AiProviderView[] }): ReactNode {
  const [editing, setEditing] = useState<AiProviderView | 'new' | null>(null);
  return (
    <Card
      title="Providers"
      description="Where models are reached. Keys are stored encrypted and never shown again."
      aside={
        editing === null ? (
          <Button
            variant="primary"
            onClick={() => {
              setEditing('new');
            }}
          >
            New provider
          </Button>
        ) : undefined
      }
    >
      {editing !== null && (
        <ProviderEditor
          provider={editing === 'new' ? null : editing}
          onDone={() => {
            setEditing(null);
          }}
        />
      )}
      {providers.length === 0 && editing === null && (
        <EmptyState title="No providers yet">
          Add a provider: OpenAI, Anthropic, Google Gemini, Mistral, Groq, xAI, DeepSeek, the Vercel
          AI Gateway (one key for many vendors), Azure OpenAI, any OpenAI-compatible service such as
          OpenRouter, or a local Ollama.
        </EmptyState>
      )}
      {providers.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <caption className="visually-hidden">AI providers</caption>
            <thead>
              <tr>
                <th scope="col">Provider</th>
                <th scope="col">Address</th>
                <th scope="col">Used by</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {providers.map((provider) => (
                <ProviderRow
                  key={provider.id}
                  provider={provider}
                  onEdit={() => {
                    setEditing(provider);
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function ProviderRow({
  provider,
  onEdit,
}: {
  provider: AiProviderView;
  onEdit: () => void;
}): ReactNode {
  const { ensureReauthenticated } = useReauth();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [model, setModel] = useState('');
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const remove = useMutation({
    mutationFn: () => deleteAiProvider(provider.id),
    onSuccess: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ['ai-providers'] });
    },
    onError: (caught: unknown) => {
      setConfirming(false);
      setNote({ ok: false, text: errorText(caught, 'Could not delete the provider.') });
    },
  });
  const test = useMutation({
    mutationFn: () => testAiProvider(provider.id, model.trim()),
    onSuccess: (result) => {
      setNote({
        ok: true,
        text: `Answered in ${(result.ms / 1000).toFixed(1)} s (${String(result.inputTokens ?? '?')} tokens in, ${String(result.outputTokens ?? '?')} out).`,
      });
      void queryClient.invalidateQueries({ queryKey: ['ai-usage'] });
    },
    onError: (caught: unknown) => {
      setNote({ ok: false, text: errorText(caught, 'The test failed.') });
      void queryClient.invalidateQueries({ queryKey: ['ai-usage'] });
    },
  });

  return (
    <tr className={provider.enabled ? undefined : 'row-done'}>
      <td>
        {provider.name}
        <span className="cell-sub">
          {kindLabel(provider.kind)} · {provider.enabled ? 'on' : 'off'} ·{' '}
          {provider.metered ? 'metered' : 'not metered'}
          {provider.hasKey && ' · key stored'}
        </span>
      </td>
      <td className="text-sm">{addressOf(provider)}</td>
      <td className="text-sm">
        {provider.usedBy.length === 0
          ? '—'
          : provider.usedBy.map((task) => TASK_LABELS[task].label).join(', ')}
      </td>
      <td>
        <div className="row">
          <Button variant="ghost" onClick={onEdit}>
            Edit
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setConfirming(true);
            }}
          >
            Delete
          </Button>
        </div>
        <form
          className="row"
          onSubmit={(event) => {
            event.preventDefault();
            setNote(null);
            test.mutate();
          }}
        >
          <TextField
            label={`Model to test on ${provider.name}`}
            placeholder={AI_PROVIDER_KINDS[provider.kind].exampleModel}
            required
            value={model}
            onChange={(event) => {
              setModel(event.target.value);
            }}
          />
          <Button type="submit" busy={test.isPending} busyLabel="Testing…">
            Test
          </Button>
        </form>
        {note !== null && (
          <span
            className={note.ok ? 'cell-sub' : 'field-error'}
            role={note.ok ? undefined : 'alert'}
          >
            {note.text}
          </span>
        )}
        {confirming && (
          <ConfirmDialog
            title={`Delete “${provider.name}”?`}
            description="Its key is deleted with it. A provider that routing still uses cannot be deleted."
            confirmLabel="Delete the provider"
            danger
            busy={remove.isPending}
            onConfirm={() => {
              void ensureReauthenticated().then((ok) => {
                if (ok) remove.mutate();
              });
            }}
            onCancel={() => {
              setConfirming(false);
            }}
          />
        )}
      </td>
    </tr>
  );
}

function ProviderEditor({
  provider,
  onDone,
}: {
  provider: AiProviderView | null;
  onDone: () => void;
}): ReactNode {
  const { ensureReauthenticated } = useReauth();
  const queryClient = useQueryClient();
  const [name, setName] = useState(provider?.name ?? '');
  const [kind, setKind] = useState<AiProviderKind>(provider?.kind ?? 'openai');
  const info = AI_PROVIDER_KINDS[kind];
  const [baseUrl, setBaseUrl] = useState(provider?.baseUrl ?? '');
  const [apiKey, setApiKey] = useState('');
  const [removeKey, setRemoveKey] = useState(false);
  const [metered, setMetered] = useState(provider?.metered ?? true);
  const [enabled, setEnabled] = useState(provider?.enabled ?? true);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body: AiProviderInput = {
        name: name.trim(),
        kind,
        baseUrl: baseUrl.trim() === '' ? null : baseUrl.trim(),
        metered,
        enabled,
        ...(removeKey ? { apiKey: null } : apiKey === '' ? {} : { apiKey }),
      };
      return provider === null ? createAiProvider(body) : updateAiProvider(provider.id, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ai-providers'] });
      void queryClient.invalidateQueries({ queryKey: ['ai-status'] });
      onDone();
    },
    onError: (caught: unknown) => {
      setError(errorText(caught, 'Could not save the provider.'));
    },
  });

  const submit = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    if (await ensureReauthenticated()) save.mutate();
  };

  return (
    <form className="stack fieldset" onSubmit={(event) => void submit(event)}>
      <h3>{provider === null ? 'New provider' : `Edit “${provider.name}”`}</h3>
      <div className="filters">
        <TextField
          label="Name"
          hint="Lower-case letters, digits and dashes."
          required
          maxLength={40}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <SelectField
          label="Kind"
          value={kind}
          onChange={(event) => {
            const parsed = AiProviderKind.safeParse(event.target.value);
            if (parsed.success) {
              setKind(parsed.data);
              // A local model costs nothing per token; a cloud one does.
              setMetered(AI_PROVIDER_KINDS[parsed.data].meteredByDefault);
            }
          }}
        >
          {AiProviderKind.options.map((option) => (
            <option key={option} value={option}>
              {kindLabel(option)}
            </option>
          ))}
        </SelectField>
        <TextField
          label="Address"
          hint={
            info.needsAddress
              ? `${info.hint} Plain http only to this machine or a private address.`
              : `Leave empty for ${info.origin ?? 'its own address'}, or give a proxy in front of it.`
          }
          required={info.needsAddress}
          value={baseUrl}
          onChange={(event) => {
            setBaseUrl(event.target.value);
          }}
        />
        {kind === 'openai_compatible' && (
          <SelectField
            label="A known service"
            hint="Fills in its address."
            value=""
            onChange={(event) => {
              if (event.target.value !== '') setBaseUrl(event.target.value);
            }}
          >
            <option value="">Choose…</option>
            {OPENAI_COMPATIBLE_PRESETS.map((preset) => (
              <option key={preset.baseUrl} value={preset.baseUrl}>
                {preset.label}
              </option>
            ))}
          </SelectField>
        )}
      </div>
      <TextField
        label="API key"
        type="password"
        autoComplete="new-password"
        hint={
          provider?.hasKey === true
            ? 'One is stored. Type a new one to replace it. Changing the kind or address needs it typed again.'
            : info.needsKey
              ? info.hint
              : 'Optional: a local model needs none, a hosted service or a gateway in front usually does.'
        }
        disabled={removeKey}
        value={apiKey}
        onChange={(event) => {
          setApiKey(event.target.value);
        }}
      />
      {provider?.hasKey === true && !info.needsKey && (
        <label className="checkbox">
          <input
            type="checkbox"
            checked={removeKey}
            onChange={(event) => {
              setRemoveKey(event.target.checked);
            }}
          />
          Remove the stored key
        </label>
      )}
      <label className="checkbox">
        <input
          type="checkbox"
          checked={metered}
          onChange={(event) => {
            setMetered(event.target.checked);
          }}
        />
        Count its tokens against the daily budget
      </label>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => {
            setEnabled(event.target.checked);
          }}
        />
        On
      </label>
      {error !== null && <Alert tone="danger">{error}</Alert>}
      <div className="row">
        <Button type="submit" variant="primary" busy={save.isPending} busyLabel="Saving…">
          {provider === null ? 'Add provider' : 'Save provider'}
        </Button>
        <Button onClick={onDone}>Cancel</Button>
      </div>
    </form>
  );
}

interface RouteDraft {
  providerId: string;
  model: string;
  fallbackProviderId: string;
  fallbackModel: string;
}

const draftOf = (route: AiRoute | undefined): RouteDraft => ({
  providerId: route?.providerId ?? '',
  model: route?.model ?? '',
  fallbackProviderId: route?.fallback?.providerId ?? '',
  fallbackModel: route?.fallback?.model ?? '',
});

function RoutingCard({
  routes,
  providers,
}: {
  routes: AiRoute[];
  providers: AiProviderView[];
}): ReactNode {
  const { ensureReauthenticated } = useReauth();
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<AiRouteTask, RouteDraft>>(
    () =>
      Object.fromEntries(
        AiRouteTask.options.map((task) => [
          task,
          draftOf(routes.find((route) => route.task === task)),
        ]),
      ) as Record<AiRouteTask, RouteDraft>,
  );
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      saveAiRoutes({
        routes: AiRouteTask.options.flatMap((task) => {
          const draft = drafts[task];
          if (draft.providerId === '' || draft.model.trim() === '') return [];
          return [
            {
              task,
              providerId: draft.providerId,
              model: draft.model.trim(),
              fallback:
                draft.fallbackProviderId === '' || draft.fallbackModel.trim() === ''
                  ? null
                  : { providerId: draft.fallbackProviderId, model: draft.fallbackModel.trim() },
            },
          ];
        }),
      }),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['ai-routes'] });
      void queryClient.invalidateQueries({ queryKey: ['ai-providers'] });
      void queryClient.invalidateQueries({ queryKey: ['ai-status'] });
    },
    onError: (caught: unknown) => {
      setError(errorText(caught, 'Could not save the routing.'));
    },
  });

  const update = (task: AiRouteTask, change: Partial<RouteDraft>): void => {
    setDrafts((current) => ({ ...current, [task]: { ...current[task], ...change } }));
  };

  const submit = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSaved(false);
    if (await ensureReauthenticated()) save.mutate();
  };

  const options = (
    <>
      {providers.map((provider) => (
        <option key={provider.id} value={provider.id}>
          {provider.name}
        </option>
      ))}
    </>
  );

  return (
    <Card
      title="Routing"
      description="Which model each task uses, and what to try when it fails, is too slow or the budget is spent. A task without a route uses Default."
    >
      {providers.length === 0 ? (
        <EmptyState title="Add a provider first">Routes name a provider and a model.</EmptyState>
      ) : (
        <form onSubmit={(event) => void submit(event)} className="stack">
          {AiRouteTask.options.map((task) => (
            <fieldset key={task} className="fieldset stack">
              <legend>{TASK_LABELS[task].label}</legend>
              <span className="field-hint">{TASK_LABELS[task].hint}</span>
              <div className="filters">
                <SelectField
                  label={`${TASK_LABELS[task].label}: provider`}
                  value={drafts[task].providerId}
                  onChange={(event) => {
                    update(task, { providerId: event.target.value });
                  }}
                >
                  <option value="">{task === 'default' ? 'Not set' : 'Use Default'}</option>
                  {options}
                </SelectField>
                <TextField
                  label={`${TASK_LABELS[task].label}: model`}
                  value={drafts[task].model}
                  onChange={(event) => {
                    update(task, { model: event.target.value });
                  }}
                />
                <SelectField
                  label={`${TASK_LABELS[task].label}: fallback provider`}
                  value={drafts[task].fallbackProviderId}
                  onChange={(event) => {
                    update(task, { fallbackProviderId: event.target.value });
                  }}
                >
                  <option value="">None</option>
                  {options}
                </SelectField>
                <TextField
                  label={`${TASK_LABELS[task].label}: fallback model`}
                  value={drafts[task].fallbackModel}
                  onChange={(event) => {
                    update(task, { fallbackModel: event.target.value });
                  }}
                />
              </div>
            </fieldset>
          ))}
          {error !== null && <Alert tone="danger">{error}</Alert>}
          {saved && <Alert tone="success">Saved.</Alert>}
          <div className="row">
            <Button type="submit" variant="primary" busy={save.isPending} busyLabel="Saving…">
              Save routing
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

function UsageCard(): ReactNode {
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const usage = useQuery({ queryKey: ['ai-usage'], queryFn: getAiUsage });
  if (usage.data === undefined) return null;
  const { used, budget, days, recent } = usage.data;
  return (
    <Card title="Usage" description="Every call, including the ones the budget refused.">
      <p>
        Today (UTC): <strong>{tokens(used)}</strong> of {tokens(budget)} metered tokens.
      </p>
      {days.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <caption className="visually-hidden">AI usage by day</caption>
            <thead>
              <tr>
                <th scope="col">Day (UTC)</th>
                <th scope="col">Metered tokens</th>
                <th scope="col">Local tokens</th>
                <th scope="col">Calls</th>
              </tr>
            </thead>
            <tbody>
              {days.map((day) => (
                <tr key={day.day}>
                  <td className="text-sm">{day.day}</td>
                  <td className="text-sm">{tokens(day.meteredTokens)}</td>
                  <td className="text-sm">{tokens(day.unmeteredTokens)}</td>
                  <td className="text-sm">{day.calls}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {recent.length === 0 ? (
        <EmptyState title="Nothing asked yet">Calls appear here as they are made.</EmptyState>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <caption className="visually-hidden">Recent AI calls</caption>
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Task</th>
                <th scope="col">Model</th>
                <th scope="col">Tokens</th>
                <th scope="col">Result</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((call) => (
                <tr key={call.id}>
                  <td className="text-sm" title={formatInstant(call.createdAt, { timezone })}>
                    {formatRelative(call.createdAt)}
                    {call.requestedByEmail !== null && (
                      <span className="cell-sub">{call.requestedByEmail}</span>
                    )}
                  </td>
                  <td className="text-sm">
                    {call.task === 'test' ? 'Provider test' : TASK_LABELS[call.task].label}
                  </td>
                  <td className="text-sm">
                    {call.providerName} · {call.model}
                    {call.fallback && <span className="cell-sub">fallback</span>}
                  </td>
                  <td className="text-sm">
                    {call.inputTokens === null
                      ? '—'
                      : `${tokens(call.inputTokens)} in, ${tokens(call.outputTokens ?? 0)} out`}
                  </td>
                  <td className="text-sm">
                    <span className={`badge ${STATUS_BADGE[call.status]}`}>{call.status}</span>
                    {call.error !== null && <span className="cell-sub">{call.error}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
